import { useEffect, useRef, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { RefObject } from 'react'
import type { AccountWithStats } from '@olimhub/shared'
import { Avatar } from '@/components/profile/Avatar'

interface MentionDropdownProps {
  results: AccountWithStats[]
  selectedIdx: number
  onSelect: (username: string) => void
  onClose: () => void
  anchorRef: RefObject<HTMLTextAreaElement | null>
}

export function MentionDropdown({ results, selectedIdx, onSelect, onClose, anchorRef }: MentionDropdownProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)

  // Re-measure on every render so position tracks scroll and resize.
  // Use functional setState so the same object reference is kept when coords
  // haven't changed — this breaks the render → setPos → render loop.
  useLayoutEffect(() => {
    const el = anchorRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const top = rect.bottom + 4
    const { left, width } = rect
    setPos((prev) =>
      prev && prev.top === top && prev.left === left && prev.width === width
        ? prev
        : { top, left, width },
    )
  })

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  if (results.length === 0 || !pos) return null

  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed',
        zIndex: 9999,
        top: pos.top,
        left: pos.left,
        width: pos.width,
        background: 'var(--surface)',
        border: '1.5px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-modal)',
        overflow: 'hidden',
      }}
    >
      {results.map((acc, i) => (
        <button
          key={acc.id}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onSelect(acc.username) }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            background: i === selectedIdx ? 'var(--brand-soft)' : 'transparent',
            cursor: 'pointer',
            textAlign: 'start',
            transition: 'background var(--dur-fast)',
          }}
        >
          <Avatar
            name={acc.displayName || acc.username}
            avatarUrl={acc.avatarUrl}
            animalAvatar={acc.animalAvatar}
            accountType={acc.accountType}
            size="sm"
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {acc.displayName || acc.username}
            </div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
              @{acc.username}
            </div>
          </div>
        </button>
      ))}
    </div>,
    document.body,
  )
}
